import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { getUserTabs } from "../Common/helpers";
import { useAdminList } from "Api/Coordinator/Gebruikers";
import { useCommunityAdminList, useGebruikerDetail } from "Api/Coordinator/Gebruikers/GebruikerView";

export const GebruikerContext = createContext();

export function useGebruikerContext() {
	const context = useContext(GebruikerContext);
	if (!context) throw new Error("Use gebruikers context within provider.");

	return context;
}

export const GebruikersProvider = ({ children }) => {
	const location = useLocation();
	const [activeTab, setActiveTab] = useState("");
	const [filterCommunity, setFilterCommunity] = useState([]);
	const [adminCommunityList, setAdminCommunityList] = useState([]);
	const [userDetails, setUserDetails] = useState();
	const [isLoading, setLoader] = useState(false);
	const [tabsLoader, setTabsLoader] = useState(false);
	const [currentUser, setCurrentUser] = useState(location.state || {});
	const [disable, setDisable] = useState(false)

	const {
		trigger: getAdminList,
		data: adminResponse,
	} = useAdminList();

	const userTabs = useMemo(() => {
		const isSubstitutor = adminCommunityList.length > 0;
		return getUserTabs(location.pathname, isSubstitutor, disable);
	}, [location.pathname, adminCommunityList, disable]);

	const params = useParams();
	const gebruikerId = params?.gebruikerId;
	const navigate = useNavigate();

	const {
		data: communityAdminResponse,
		isLoading: communityAdminLoading,
		mutate: mutateCommunityAdmin,
	} = useCommunityAdminList(gebruikerId);

	const {
		trigger: getGebruikerDetail,
	} = useGebruikerDetail();

	useEffect(() => {
		if (!communityAdminResponse) return;
		if (
			communityAdminResponse.status === false &&
			communityAdminResponse.error === "invalid_link"
		) {
			navigate("/no-access");
			return;
		}

		if (communityAdminResponse.status) {
			setAdminCommunityList(communityAdminResponse.data || []);
		}

		setTabsLoader(false);
	}, [communityAdminResponse, navigate]);

	const fetchCurrentUserDetails = () => {
		const payload = {
			iCommunityId: "",
			userType: "",
			contractType: "",
			pageSize: 20,
			search: "",
			page: 1,
			sortBy: "",
			sort: "",
			iAdminId: parseInt(gebruikerId),
		};
		getAdminList({
			data: payload,
		})
			.then((response) => {
				
				if (response.status == true) {
					setCurrentUser((prev) => ({
						...prev,
						data: {
							...prev.data,
							clooserId: response.data?.list[0]?.clooserId,
							communityList: response.data?.list?.flatMap(
								(item) => item.communityList
							),
							name: response.data?.list[0]?.name,
							plaats: response.data?.list[0]?.plaats,
						},
					}));
				}
			})
			.catch(() => console.error);
	};

	const fetchUserDetails = async (limit = null) => {
		if (
			activeTab === userTabs.Functies ||
			activeTab === userTabs.Beschikbaarheid ||
			activeTab === userTabs.Matchcodes ||
			activeTab === userTabs.Urenoverzicht ||
			activeTab === userTabs.Controlscherm
		) {
			return;
		}

		const payload = {
			iAdminId: parseInt(gebruikerId),
			iCommunityIds: filterCommunity.map((item) => item?.iCommunityId).join(","),
			type: activeTab,
			disableOrg: "",
		};


		const response = await getGebruikerDetail({
			data: payload,
			...(limit ? {
				params: {
					limit,
				},
			} : {}),
		});

		if (response?.data && response?.data) {
			setUserDetails(response.data);

			const user = JSON.parse(localStorage.getItem("user"));
			const loggedAdminId = user?.iAdminId;

			if (loggedAdminId === parseInt(gebruikerId)) {
				setDisable(false);
			} else {
				setDisable(
					response.data?.profileList?.isCoordinator === 1 ||
					response.data?.isCoordinator
				);
			}
		}
	};

	const isActiveTab = (currentTab) => {
		return activeTab === currentTab;
	};

	const fetchAdminCommunityList = async () => {
		if (!gebruikerId) return;
		await mutateCommunityAdmin();
	};

	useEffect(() => {
		fetchCurrentUserDetails();

	}, []);

	useEffect(() => {
		if (location.state?.tab) {
			setActiveTab(location.state.tab);
		} else if (Object.values(userTabs).length) {

			setActiveTab(Object.values(userTabs)[0]);
		}
	}, []);

	useEffect(() => {
		if (!activeTab || !currentUser) return;

		if (activeTab === userTabs.Profiel || activeTab === userTabs.Algemeen) {
			if (currentUser?.data?.communityList?.length) {
				setFilterCommunity([...currentUser.data.communityList]);
			}
		} else if (activeTab === userTabs.Matchcodes || activeTab === userTabs.Urenoverzicht) {
			setFilterCommunity(adminCommunityList);
		} else {
			setFilterCommunity([adminCommunityList[0]]);
		}
	}, [activeTab, currentUser, adminCommunityList]);


	useEffect(() => {
		if (!activeTab) return;


		if (activeTab === userTabs.Algemeen && filterCommunity.length === 0) {
			fetchUserDetails();
			return;
		}

		const hasValidCommunityIds = filterCommunity.some((item) => item?.iCommunityId);

		if (hasValidCommunityIds) {
			fetchUserDetails();
		}
	}, [filterCommunity, activeTab]);

	return (
		<GebruikerContext.Provider
			value={{
				activeTab,
				setActiveTab,
				filterCommunity,
				setFilterCommunity,
				adminCommunityList,
				setAdminCommunityList,
				userDetails,
				setUserDetails,
				isLoading,
				setLoader,
				tabsLoader,
				setTabsLoader,
				currentUser,
				setCurrentUser,
				fetchUserDetails,
				fetchAdminCommunityList,
				isActiveTab,
				userTabs,
				disable,
			}}
		>
			{children}
		</GebruikerContext.Provider>
	);
};
